import {
  heightFeet,
  heightInches,
  requiredEnum,
  requiredString,
  weightPounds,
  zt,
} from '@/util/validators';
import { z } from 'zod';
import { useEncounter } from '@/contexts/encounter-context';
import { useTranslation } from '@/contexts/i18n-context';
import { RadioGroup } from '@/components/inputs/radio-group';
import { Form } from '@/components/form';
import { InputLabel } from '@/components/inputs/input-label';
import { TextInput } from '@/components/inputs/text-input';
import styles from './health-and-lifestyle-page.module.css';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlcoholConsumption,
  Frequencies,
  SkinTone,
  SkinType,
  TobaccoConsumption,
  Gender,
  CreateMedicalIntake,
  GetMedicalIntakeResponseMedicalIntake,
  FemaleQuestion,
} from '@/api/api.schemas';
import { PageContainer } from '@/components/containers/page-container';
import { Section } from '@/components/containers/section';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { useMediaQuery } from '@/hooks/use-media-query';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { useAsyncAction } from '@/hooks/use-async-action';
import { Select } from '@/components/inputs/select';
import clsx from 'clsx';
import { useUpdateEncountersMedicalIntake } from '@/api/update-medical-intake';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { useSaveProgressProps } from '@/hooks/use-save-progress-props';
import { useGetEncountersMedicalIntake } from '@/api/get-medical-intake';
import { FieldSet } from '@/components/inputs/field-set';
import { parseISO } from 'date-fns';
import { Loader } from '@/components/loaders/loader';

function isDueDateWithinNineMonths(date: string) {
  const now = new Date();
  const targetDate = parseISO(date);
  const nineMonthsFromNow = new Date(now.setMonth(now.getMonth() + 9));

  return targetDate <= nineMonthsFromNow;
}

function isPastDueDate(date: string) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const targetDate = parseISO(date);
  targetDate.setHours(0, 0, 0, 0);

  return targetDate < now;
}
const UterusOptions = {
  Yes: 'Yes',
  No: 'No',
} as const;

const MedicalIntakeSchema = z
  .object({
    skinType: requiredEnum(SkinType),
    skinTone: requiredEnum(SkinTone),
    heightFeet: heightFeet(),
    heightInches: heightInches(),
    weight: weightPounds(),
    alcoholConsumption: requiredEnum(AlcoholConsumption),
    tobaccoConsumption: requiredEnum(TobaccoConsumption),
    timeOutdoorsFrequency: requiredEnum(Frequencies),
    sunbathFrequency: requiredEnum(Frequencies),
    occupation: requiredString(),
    visitGender: requiredEnum(Gender),
    visitGenderOther: z.string().optional(),
    uterus: requiredEnum(UterusOptions),
    femaleQuestion: z.nativeEnum(FemaleQuestion).nullish(),
    dueDate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.uterus === UterusOptions.Yes && !data.femaleQuestion) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['femaleQuestion'],
        message: zt('common.errors.required'),
      });
    }

    if (data.visitGender === Gender.other && !data.visitGenderOther?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visitGenderOther'],
        message: zt('common.errors.required'),
      });
    }

    if (data.femaleQuestion === FemaleQuestion.Pregnant) {
      if (!data.dueDate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dueDate'],
          message: zt('common.errors.required'),
        });
        return;
      }

      if (!isDueDateWithinNineMonths(data.dueDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dueDate'],
          message: zt(
            `${translationBase}.reproductiveHealthSection.femaleQuestion.pregnancyDueDateExceedError`,
          ),
        });
      }

      if (isPastDueDate(data.dueDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dueDate'],
          message: zt(
            `${translationBase}.reproductiveHealthSection.femaleQuestion.pregnancyPastDueDateError`,
          ),
        });
      }
    }
  });

export type MedicalIntakeFormValues = z.infer<typeof MedicalIntakeSchema>;
type DefaultMedicalIntakeFormValues = Partial<MedicalIntakeFormValues>;

const baseDefaultValues: DefaultMedicalIntakeFormValues = {
  skinType: undefined,
  skinTone: undefined,
  heightFeet: undefined,
  heightInches: undefined,
  weight: undefined,
  alcoholConsumption: undefined,
  tobaccoConsumption: undefined,
  timeOutdoorsFrequency: undefined,
  sunbathFrequency: undefined,
  occupation: undefined,
  uterus: undefined,
  femaleQuestion: undefined,
  dueDate: undefined,
};

const translationBase = 'visit.healthAndLifestyle';
const translationBaseSkinTypeAndRiskFactors = `${translationBase}.skinTypeAndRiskFactorsSection`;
const translationBaseReproductiveHealth = `${translationBase}.reproductiveHealthSection`;

export function HealthAndLifestylePage() {
  const { encounter } = useEncounter();
  const { data: medicalIntake, isFetching: isMedicalIntakeFetching } =
    useGetEncountersMedicalIntake(encounter.id);

  if (isMedicalIntakeFetching) {
    return <Loader type="large" />;
  }

  return <HealthAndLifestyleForm medicalIntake={medicalIntake?.medicalIntake} />;
}

interface HealthAndLifestyleFormProps {
  medicalIntake?: GetMedicalIntakeResponseMedicalIntake;
}

function HealthAndLifestyleForm({ medicalIntake }: HealthAndLifestyleFormProps) {
  const { t } = useTranslation();
  const { encounter, invalidateEncounter } = useEncounter();
  const { navigatePostSubmit } = usePostSubmitNavigation();
  const { mutateAsync: saveMedicalIntake } = useUpdateEncountersMedicalIntake();

  const defaultValues: DefaultMedicalIntakeFormValues = medicalIntake
    ? {
        skinType: (Object.values(SkinType) as string[]).includes(medicalIntake.skinType)
          ? (medicalIntake.skinType as SkinType)
          : undefined,
        skinTone: medicalIntake.skinTone ?? undefined,
        weight: medicalIntake.weight.toString(),
        heightFeet: medicalIntake.heightFeet.toString(),
        heightInches: medicalIntake.heightInches.toString(),
        alcoholConsumption: medicalIntake.alcoholConsumption ?? undefined,
        tobaccoConsumption: medicalIntake.tobaccoConsumption ?? undefined,
        timeOutdoorsFrequency: medicalIntake.timeOutdoorsFrequency ?? undefined,
        sunbathFrequency: medicalIntake.sunbathFrequency ?? undefined,
        occupation: medicalIntake.occupation ?? undefined,
        uterus: medicalIntake.uterus ? UterusOptions.Yes : UterusOptions.No,
        visitGender: medicalIntake.visitGender,
        visitGenderOther: medicalIntake.visitGenderOther ?? undefined,
        femaleQuestion: medicalIntake.femaleQuestion,
        dueDate: medicalIntake.dueDate ?? undefined,
      }
    : {
        ...baseDefaultValues,
      };

  const form = useForm<MedicalIntakeFormValues>({
    resolver: zodResolver(MedicalIntakeSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues,
  });

  const { trigger: saveMedicalIntakeAsync, loading: isSavingMedicalIntake } = useAsyncAction(
    async (data: CreateMedicalIntake, isIncomplete: boolean) => {
      await saveMedicalIntake({
        encounterId: encounter.id,
        data: {
          medicalIntake: data,
          incomplete: isIncomplete,
        },
      });
      await invalidateEncounter();

      navigatePostSubmit({ isIncomplete });
    },
  );

  const onSubmit = (data: MedicalIntakeFormValues, isIncomplete: boolean) => {
    const uterus = data.uterus === UterusOptions.Yes;
    const femaleQuestion = uterus ? data.femaleQuestion : undefined;
    const dueDate = femaleQuestion === FemaleQuestion.Pregnant ? data.dueDate : undefined;

    const cleanedData: CreateMedicalIntake = {
      ...data,
      weight: Number(data.weight),
      heightFeet: Number(data.heightFeet),
      heightInches: Number(data.heightInches),
      uterus,
      femaleQuestion,
      dueDate,
    };

    saveMedicalIntakeAsync(cleanedData, isIncomplete);
  };

  const isMobile = useMediaQuery('--screen-md');

  const uterusValue = form.watch('uterus');
  const hasUterus = uterusValue === UterusOptions.Yes;

  const femaleQuestionValue = form.watch('femaleQuestion');
  const isPregnant = hasUterus && femaleQuestionValue === FemaleQuestion.Pregnant;

  const visitGenderValue = form.watch('visitGender');
  const isOtherGender = visitGenderValue === Gender.other;

  const saveProgressProps = useSaveProgressProps(form, (data) => onSubmit(data, true));

  return (
    <PageContainer>
      {!isMobile && <VisitTitleButtons saveProgress={saveProgressProps} />}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons />}
        <VisitNavigation />
        <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        <p>{t(`${translationBase}.description`)}</p>
        {isMobile && <SaveProgressButton {...saveProgressProps} />}
      </GradientFormTitle>

      <Form onSubmit={(data) => onSubmit(data, false)} form={form}>
        <Section className={styles.formContainer}>
          <div className={styles.sectionHeader}>
            <h2 className="h4">{t(`${translationBaseSkinTypeAndRiskFactors}.title`)}</h2>
            <p className={clsx('body-2', styles.description)}>
              {t(`${translationBaseSkinTypeAndRiskFactors}.description`)}
            </p>
          </div>
          <RadioGroup
            name="skinType"
            label={t(`${translationBaseSkinTypeAndRiskFactors}.skinType.label`)}
            className={styles.skinTypeGroup}
            options={Object.values(SkinType).map((skinType) => ({
              value: skinType,
              label: t(`${translationBaseSkinTypeAndRiskFactors}.skinType.options.${skinType}`),
            }))}
          />

          <RadioGroup
            name="skinTone"
            variant="chip"
            label={t(`${translationBaseSkinTypeAndRiskFactors}.skinTone.label`)}
            className={styles.skinToneGroup}
            options={Object.values(SkinTone).map((skinTone) => ({
              value: skinTone,
              label: t(`${translationBaseSkinTypeAndRiskFactors}.skinTone.options.${skinTone}`),
              className: styles[skinTone],
            }))}
          />

          <div className={styles.additionalInformationContainer}>
            <FieldSet className={styles.heightContainer}>
              <InputLabel
                label={t(`${translationBaseSkinTypeAndRiskFactors}.height.label`)}
                as="legend"
              />
              <div className={styles.heightGroup}>
                <TextInput
                  name="heightFeet"
                  type="number"
                  aria-label={t(`${translationBaseSkinTypeAndRiskFactors}.height.ariaLabelFeet`)}
                  withIconsIndicators={false}
                  min={0}
                  roundNumbers
                  suffix={t(`${translationBaseSkinTypeAndRiskFactors}.height.feetSuffix`)}
                  placeholder={t(`${translationBaseSkinTypeAndRiskFactors}.height.feetPlaceholder`)}
                />
                <TextInput
                  name="heightInches"
                  type="number"
                  aria-label={t(`${translationBaseSkinTypeAndRiskFactors}.height.ariaLabelInches`)}
                  withIconsIndicators={false}
                  min={0}
                  roundNumbers
                  suffix={t(`${translationBaseSkinTypeAndRiskFactors}.height.inchesSuffix`)}
                  placeholder={t(
                    `${translationBaseSkinTypeAndRiskFactors}.height.inchesPlaceholder`,
                  )}
                />
              </div>
            </FieldSet>
            <TextInput
              name="weight"
              type="number"
              min={0}
              label={t(`${translationBaseSkinTypeAndRiskFactors}.weight.label`)}
              placeholder={t(`${translationBaseSkinTypeAndRiskFactors}.weight.placeholder`)}
              withIconsIndicators={false}
              roundNumbers
              suffix={t(`${translationBaseSkinTypeAndRiskFactors}.weight.suffix`)}
            />

            <Select
              name="alcoholConsumption"
              label={t(`${translationBaseSkinTypeAndRiskFactors}.alcoholConsumption.label`)}
              placeholder={t(`${translationBase}.selectFrequency`)}
              options={Object.values(AlcoholConsumption).map((alcoholConsumption) => ({
                value: alcoholConsumption,
                label: t(
                  `${translationBaseSkinTypeAndRiskFactors}.alcoholConsumption.options.${alcoholConsumption}`,
                ),
              }))}
            />

            <Select
              name="tobaccoConsumption"
              label={t(`${translationBaseSkinTypeAndRiskFactors}.tobaccoConsumption.label`)}
              placeholder={t(`${translationBase}.selectFrequency`)}
              options={Object.values(TobaccoConsumption).map((tobaccoConsumption) => ({
                value: tobaccoConsumption,
                label: t(
                  `${translationBaseSkinTypeAndRiskFactors}.tobaccoConsumption.options.${tobaccoConsumption}`,
                ),
              }))}
            />

            <Select
              name="timeOutdoorsFrequency"
              label={t(`${translationBaseSkinTypeAndRiskFactors}.timeOutdoors`)}
              placeholder={t(`${translationBase}.selectFrequency`)}
              options={Object.values(Frequencies).map((timeOutdoors) => ({
                value: timeOutdoors,
                label: t(`${translationBaseSkinTypeAndRiskFactors}.frequencies.${timeOutdoors}`),
              }))}
            />

            <Select
              name="sunbathFrequency"
              label={t(`${translationBaseSkinTypeAndRiskFactors}.sunbathFrequency`)}
              placeholder={t(`${translationBase}.selectFrequency`)}
              options={Object.values(Frequencies).map((sunbathFrequency) => ({
                value: sunbathFrequency,
                label: t(
                  `${translationBaseSkinTypeAndRiskFactors}.frequencies.${sunbathFrequency}`,
                ),
              }))}
            />
            <TextInput
              name="occupation"
              label={t(`${translationBaseSkinTypeAndRiskFactors}.occupation.label`)}
              withIconsIndicators={false}
              placeholder={t(`${translationBaseSkinTypeAndRiskFactors}.occupation.placeholder`)}
            />
          </div>
        </Section>

        <Section className={styles.formContainer}>
          <div className={styles.sectionHeader}>
            <h3 className="h4">{t(`${translationBaseReproductiveHealth}.title`)}</h3>
            <p className={clsx('body-2', styles.description)}>
              {t(`${translationBaseReproductiveHealth}.description`)}
            </p>
          </div>

          <RadioGroup
            name="visitGender"
            variant="selector"
            label={t(`${translationBaseReproductiveHealth}.genderIdentity`)}
            subLabel={t(`${translationBaseReproductiveHealth}.genderIdentityHelper`)}
            options={Object.values(Gender).map((gender) => ({
              value: gender,
              label: t(`common.gender.${gender}`),
            }))}
          />

          {isOtherGender && (
            <div className={styles.optionalInput}>
              <TextInput
                name="visitGenderOther"
                label={t(`${translationBaseReproductiveHealth}.genderIdentityOther`)}
                placeholder={t(
                  `${translationBaseReproductiveHealth}.genderIdentityOtherPlaceholder`,
                )}
              />
            </div>
          )}
          <RadioGroup
            name="uterus"
            variant="selector"
            label={t(`${translationBaseReproductiveHealth}.uterus`)}
            subLabel={t(`${translationBaseReproductiveHealth}.uterusHelper`)}
            options={Object.values(UterusOptions).map((uterus) => ({
              value: uterus,
              label: t(`${translationBaseReproductiveHealth}.uterusOptions.${uterus}`),
            }))}
          />

          {hasUterus && (
            <RadioGroup
              name="femaleQuestion"
              label={t(`${translationBaseReproductiveHealth}.femaleQuestion.label`)}
              className={styles.femaleQuestionGroup}
              options={Object.values(FemaleQuestion).map((femaleQuestion) => ({
                value: femaleQuestion,
                label: t(
                  `${translationBaseReproductiveHealth}.femaleQuestion.options.${femaleQuestion}`,
                ),
              }))}
            />
          )}

          {isPregnant && (
            <div className={styles.optionalInput}>
              <TextInput
                name="dueDate"
                label={t(`${translationBaseReproductiveHealth}.femaleQuestion.dueDateLabel`)}
                type="date"
                withIconsIndicators={false}
              />
            </div>
          )}
        </Section>
        <VisitFormButtonRow
          nextStep={t(`${translationBase}.nextStep`)}
          buttonTextKey="common.next"
          type="submit"
          rightIcon="arrow_forward"
          loading={isSavingMedicalIntake}
        />
      </Form>
    </PageContainer>
  );
}
